// Place mode (spec 2026-09-29-farm-4a-objects-design.md §2): the object follows the pointer over
// a green/red footprint, a click (a second tap on a phone) places or moves it, "↻ Odwróć" mirrors
// it, "✕" / ESC cancels. Fence mode: a see-through fence follows the pointer; press-drag a straight
// run (drawn as a ghost too), release builds it. The scene owns
// applying ops; this only decides what they are.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { cellFToLocal, cellCentre, CROP_BASE_Y, TILE_W } from './iso.mjs';
import { canPlace, fenceRuns, fencePreview, fenceGhostCells, footprintOf, objectTextureKey, objectAnchor } from './farmObjects.mjs';
import { drawFenceCell } from './FarmObjectLayer.js';
import { UI_DEPTH } from '../ui/ySort.js';
import { gameButton } from '../ui/gameUi.js';
import { iconText } from '../ui/iconText.js';

const FONT = 'system-ui, -apple-system, sans-serif';
const specOf = (item) => { const o = gameState.raw?.catalog?.farm?.objects ?? {}; return item?.startsWith('plaque:') ? o.plaque : o[item]; };
const texOf = (scene, item, kind) => {
  const key = objectTextureKey(item, gameState.raw?.catalog?.farm?.objects);   // a tree: its ripe look
  return scene.textures.exists(key) ? key : ({ tree: 'farm_obj_ph_tree', landmark: 'farm_obj_ph_landmark', hay: 'farm_obj_ph_hay', plaque: 'farm_obj_ph_plaque', fence: 'farm_obj_ph_fence_post' }[kind] ?? 'farm_obj_ph_decoration');
};

export class FarmPlacer {
  constructor(scene, { apply, onDone, toast }) {
    this.scene = scene; this.apply = apply; this.onDone = onDone; this.toast = toast;
    this.mode = null;
    this._gfx = null; this._ghost = null; this._ui = []; this._label = null; this._at = null; this._armed = null;
    this._fenceGhost = [];
  }

  get active() { return !!this.mode; }

  start(item) { this._begin({ kind: 'place', item, flip: false }); }
  startMove(p) { this._begin({ kind: 'move', id: p.id, item: p.item, flip: !!p.flip, startFlip: !!p.flip }); this.onHover({ x: p.x + specOf(p.item).w - 1, y: p.y + specOf(p.item).h - 1 }); }
  startFence() { this._begin({ kind: 'fence' }); }

  _begin(mode) {
    this.cancel(true);
    this.mode = mode;
    const world = this.scene.world;
    this._gfx = this.scene.add.graphics().setDepth(1e8);
    world.objects.add(this._gfx);
    if (mode.kind !== 'fence') {
      const spec = specOf(mode.item);
      this._ghost = this.scene.add.image(0, 0, texOf(this.scene, mode.item, spec.kind)).setOrigin(0.5, 1).setAlpha(0.6).setVisible(false).setFlipX(mode.flip).setDepth(1e8 + 1);
      const src = this._ghost.texture.getSourceImage();
      const real = !this._ghost.texture.key.startsWith('farm_obj_ph_');   // real art (objects AND trees): natural size
      if (real) this._ghost.setDisplaySize(src.width * 0.5, src.height * 0.5);
      else { const w = ((spec.w + spec.h) / 2) * TILE_W * 0.9; this._ghost.setDisplaySize(w, w * 0.8); }
      world.objects.add(this._ghost);
    }
    this._label = iconText(this.scene, 0, 0, '', { fontFamily: FONT, fontSize: '16px', color: '#fff', fontStyle: 'bold', backgroundColor: '#000000aa', padding: { x: 6, y: 3 } }).setOrigin(0.5, 1).setDepth(1e9).setVisible(false);
    world.objects.add(this._label);
    const y = GAME_HEIGHT - 100;
    const btn = (x, label, fn, variant) => {
      this._ui.push(gameButton(this.scene, x, y, 150, 46, label, fn, { variant, fontSize: 18 }).setScrollFactor(0).setDepth(UI_DEPTH + 5));
    };
    if (mode.kind !== 'fence') btn(GAME_WIDTH / 2 - 85, '{flip} Odwróć', () => { this.mode.flip = !this.mode.flip; this._ghost.setFlipX(this.mode.flip); }, 'secondary');
    btn(GAME_WIDTH / 2 + (mode.kind === 'fence' ? 0 : 85), '{cross}', () => this.cancel(), 'danger');
  }

  _paint(cells, ok) {
    const g = this._gfx; g.clear();
    const size = this.scene.world.size;
    g.fillStyle(ok ? 0x7ed957 : 0xe63946, 0.45); g.lineStyle(2, ok ? 0x4caf50 : 0xb71c1c, 1);
    for (const c of cells) {
      const pts = [cellFToLocal(c.x, c.y, size), cellFToLocal(c.x + 1, c.y, size), cellFToLocal(c.x + 1, c.y + 1, size), cellFToLocal(c.x, c.y + 1, size)];
      g.fillPoints(pts, true); g.strokePoints(pts, true);
    }
  }

  // The fence ghost: the built fence's own drawing, see-through and above everything, cells kept in
  // depth order among themselves (x + y grows towards the viewer).
  _showFence(cells) {
    this._clearFence();
    const size = this.scene.world.size;
    for (const c of fenceGhostCells(gameState.raw, cells)) {
      const box = drawFenceCell(this.scene, c.x, c.y, c.joins, size).setAlpha(0.6).setDepth(1e8 + 1 + (c.x + c.y) / 1000);
      this.scene.world.objects.add(box);
      this._fenceGhost.push(box);
    }
  }

  _clearFence() { for (const b of this._fenceGhost) b.destroy(); this._fenceGhost = []; }

  onHover(cell) {
    if (this.mode?.kind === 'fence') {
      if (this._from) return;                                         // a drag draws its own run
      if (!cell) { this._gfx.clear(); this._clearFence(); return; }
      this._paint([cell], canPlace(gameState.raw, 'plot', cell.x, cell.y));
      this._showFence([cell]);
      return;
    }
    if (!this.mode || !cell) return;
    const spec = specOf(this.mode.item);
    const x = cell.x - spec.w + 1, y = cell.y - spec.h + 1;          // the pointer is the front corner
    this._at = { x, y };
    const ok = canPlace(gameState.raw, this.mode.item, x, y, this.mode.id ?? null);
    this._paint(footprintOf(spec, x, y), ok);
    const a = objectAnchor(spec, x, y);                               // where it will stand (the layer's rule)
    const base = cellFToLocal(a.fx, a.fy, this.scene.world.size);
    this._ghost.setPosition(base.x, base.y + a.dy).setVisible(true);
  }

  onTap(cell, { wasTouch } = {}) {
    if (!this.mode || this.mode.kind === 'fence' || !cell) return;
    const key = `${cell.x},${cell.y}`;
    if (wasTouch && this._armed !== key) { this._armed = key; this.onHover(cell); return; }   // phone: first tap shows
    this._armed = null;
    this.onHover(cell);
    const { x, y } = this._at;
    const m = this.mode;
    const res = m.kind === 'place'
      ? this.apply({ op: 'place', item: m.item, x, y, flip: m.flip })
      : this.apply({ op: 'move', id: m.id, x, y });
    if (!res.ok) { this.toast(res.reason); return; }
    if (m.kind === 'move' && m.flip !== m.startFlip) this.apply({ op: 'flip', id: m.id });
    this.cancel();
  }

  onDragStart(cell) { if (this.mode?.kind === 'fence' && cell) { this._from = cell; this.onDrag(cell); } }

  onDrag(cell) {
    if (this.mode?.kind !== 'fence' || !this._from || !cell) return;
    this._runs = fenceRuns(this._from, cell);
    const cells = this._runs.flat();
    const ok = cells.every((c) => canPlace(gameState.raw, 'plot', c.x, c.y));
    this._paint(cells, ok);
    this._showFence(cells);
    const last = cells[cells.length - 1];
    const p = cellFToLocal(last.x, last.y, this.scene.world.size);
    this._label.setText(fencePreview(cells.length, specOf('plot')?.price ?? 15)).setPosition(p.x, p.y - 4).setVisible(true);
    this._fenceOk = ok;
  }

  onDragEnd() {
    if (this.mode?.kind !== 'fence' || !this._runs) return;
    if (!this._fenceOk) this.toast('Tu nie da się postawić płotu');
    else for (const run of this._runs) {
      const res = this.apply({ op: 'place_fence', cells: run.map((c) => [c.x, c.y]) });
      if (!res.ok) { this.toast(res.reason); break; }
    }
    this._from = null; this._runs = null;
    this._gfx.clear(); this._clearFence(); this._label.setVisible(false);   // fence mode stays on for the next run
  }

  cancel(silent = false) {
    if (!this.mode) return;
    this.mode = null; this._armed = null; this._from = null; this._runs = null;
    this._clearFence();
    for (const o of [this._gfx, this._ghost, this._label, ...this._ui]) o?.destroy();
    this._gfx = this._ghost = this._label = null; this._ui = [];
    if (!silent) this.onDone?.();
  }
}
